import { useEffect, useRef, useState, useCallback } from "react";
import { Box, IconButton, Typography, useTheme, Collapse } from "@mui/material";
import { Code, Eye, StarsMinimalistic } from "@solar-icons/react/ssr";

/**
 * Content Security Policy injected into the sandboxed iframe.
 * Blocks all network requests except Google Fonts (for theme font loading).
 */
const CSP_META = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline' https://fonts.googleapis.com; font-src data: https://fonts.gstatic.com; img-src data: blob:;">`;

/**
 * Google Fonts link for Inter Tight — matches the host app's MUI typography.
 */
const FONT_LINK = `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@300;400;500;600;700&display=swap" rel="stylesheet">`;

/**
 * Bridge script injected into the sandboxed iframe.
 * Provides:
 *  - window.sendPrompt(text)  → posts message to parent for chat injection
 *  - ResizeObserver            → posts content height changes to parent for auto-sizing
 */
const BRIDGE_SCRIPT = `
<script>
(function() {
    window.sendPrompt = function(text) {
        parent.postMessage({
            type: "artifact-send-prompt",
            text: String(text)
        }, "*");
    };

    function reportHeight() {
        var height = document.documentElement.scrollHeight;
        parent.postMessage({
            type: "artifact-resize",
            height: height
        }, "*");
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function() {
            setTimeout(reportHeight, 50);
        });
    } else {
        setTimeout(reportHeight, 50);
    }

    if (typeof ResizeObserver !== "undefined") {
        new ResizeObserver(function() {
            reportHeight();
        }).observe(document.documentElement);
    }

    window.addEventListener("resize", reportHeight);
})();
</script>
`;

/**
 * Design-system CSS injected into the sandboxed iframe.
 * Mirrors MakeOS's MUI theme from themes.js so the LLM's HTML looks native.
 * The LLM writes semantic HTML (button, input, table, etc.) and this CSS
 * styles them to match the app's visual identity — same approach as Claude.
 */
const THEME_CSS = `
<style>
  /* ── Design Tokens (CSS custom properties from MUI theme) ── */
  :root {
    --primary: #27C77E;
    --primary-light: #A9E8CB;
    --primary-dark: #168F58;
    --primary-contrast: #232323;
    --bg-default: #FFFFFF;
    --bg-paper: #F2F2F2;
    --text-primary: #232323;
    --text-secondary: #9E9E9E;
    --text-disabled: #DDDDDD;
    --divider: rgba(0,0,0,0.12);
    --error: #E9695E;
    --error-light: #F8D0CD;
    --warning: #CB7F0E;
    --warning-light: #FFEACB;
    --success: #27C77E;
    --success-light: #7DDDB2;
    --shadow-1: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06);
    --shadow-2: 0 3px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06);
    --radius: 8px;
    --radius-sm: 4px;
    --font: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  [data-theme="dark"] {
    --bg-default: #232323;
    --bg-paper: #464646;
    --text-primary: #FFFFFF;
    --text-secondary: #9E9E9E;
    --text-disabled: #CDCDCD;
    --divider: rgba(255,255,255,0.12);
    --shadow-1: 0 1px 3px rgba(0,0,0,0.3);
    --shadow-2: 0 3px 6px rgba(0,0,0,0.4);
  }

  /* ── Base Reset ── */
  *, *::before, *::after { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 16px;
    font-family: var(--font);
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-primary);
    background: var(--bg-default);
    -webkit-font-smoothing: antialiased;
  }

  /* ── Typography ── */
  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 12px 0;
    font-weight: 600;
    line-height: 1.3;
    color: var(--text-primary);
  }
  h1 { font-size: 24px; }
  h2 { font-size: 20px; }
  h3 { font-size: 16px; }
  p { margin: 0 0 12px 0; color: var(--text-primary); }
  small, .text-secondary { color: var(--text-secondary); }
  a { color: var(--primary-dark); text-decoration: none; }
  a:hover { text-decoration: underline; }
  hr { border: none; border-top: 1px solid var(--divider); margin: 16px 0; }

  /* ── Buttons (MUI-style) ── */
  button, .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 20px;
    font-family: var(--font);
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    border: none;
    border-radius: var(--radius);
    cursor: pointer;
    transition: background-color 0.2s, box-shadow 0.2s, transform 0.1s;
    background: var(--primary);
    color: var(--primary-contrast);
    box-shadow: none;
    text-transform: none;
  }
  button:hover, .btn:hover {
    background: var(--primary-dark);
    box-shadow: var(--shadow-1);
  }
  button:active, .btn:active { transform: scale(0.98); }
  button:disabled, .btn:disabled {
    background: var(--text-disabled);
    color: var(--text-secondary);
    cursor: not-allowed;
    box-shadow: none;
  }

  /* Button variants */
  .btn-outlined, button[data-variant="outlined"] {
    background: transparent;
    color: var(--primary);
    border: 1px solid var(--primary);
  }
  .btn-outlined:hover, button[data-variant="outlined"]:hover {
    background: rgba(39, 199, 126, 0.08);
  }
  .btn-secondary {
    background: var(--bg-paper);
    color: var(--text-primary);
    border: 1px solid var(--divider);
  }
  .btn-secondary:hover { background: var(--divider); }
  .btn-error {
    background: var(--error);
    color: white;
  }
  .btn-error:hover { background: #E34336; }
  .btn-sm { padding: 4px 12px; font-size: 13px; }

  /* ── Inputs (MUI-style outlined) ── */
  input[type="text"], input[type="number"], input[type="email"],
  input[type="password"], input[type="search"], input[type="url"],
  input[type="date"], input[type="time"], textarea, select {
    width: 100%;
    padding: 10px 14px;
    font-family: var(--font);
    font-size: 14px;
    line-height: 1.5;
    color: var(--text-primary);
    background: var(--bg-default);
    border: 1px solid var(--divider);
    border-radius: var(--radius);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  input:focus, textarea:focus, select:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(39, 199, 126, 0.2);
  }
  input::placeholder, textarea::placeholder {
    color: var(--text-secondary);
  }
  textarea { resize: vertical; min-height: 80px; }
  select { cursor: pointer; }

  /* ── Labels ── */
  label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  /* ── Checkboxes & Radios ── */
  input[type="checkbox"], input[type="radio"] {
    width: 18px;
    height: 18px;
    accent-color: var(--primary);
    cursor: pointer;
  }

  /* ── Range / Slider ── */
  input[type="range"] {
    width: 100%;
    accent-color: var(--primary);
    cursor: pointer;
  }

  /* ── Tables (MUI DataGrid feel) ── */
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  th {
    text-align: left;
    padding: 10px 14px;
    font-weight: 600;
    color: var(--text-primary);
    background: var(--bg-paper);
    border-bottom: 2px solid var(--divider);
  }
  td {
    padding: 10px 14px;
    border-bottom: 1px solid var(--divider);
    color: var(--text-primary);
  }
  tr:hover td { background: rgba(39, 199, 126, 0.04); }

  /* ── Cards / Paper ── */
  .card, .paper {
    background: var(--bg-paper);
    border-radius: var(--radius);
    padding: 16px;
    box-shadow: var(--shadow-1);
  }

  /* ── Chips / Tags ── */
  .chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 16px;
    background: var(--bg-paper);
    color: var(--text-primary);
    border: 1px solid var(--divider);
  }
  .chip-primary {
    background: var(--primary-light);
    color: var(--primary-dark);
    border-color: transparent;
  }

  /* ── Progress Bar ── */
  progress {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    border: none;
    appearance: none;
  }
  progress::-webkit-progress-bar { background: var(--bg-paper); border-radius: 3px; }
  progress::-webkit-progress-value { background: var(--primary); border-radius: 3px; }

  /* ── Alerts / Notices ── */
  .alert {
    padding: 12px 16px;
    border-radius: var(--radius);
    font-size: 13px;
    margin-bottom: 12px;
  }
  .alert-success { background: var(--success-light); color: var(--primary-dark); }
  .alert-error { background: var(--error-light); color: #B71C1C; }
  .alert-warning { background: var(--warning-light); color: #7A4B00; }

  /* ── Utility ── */
  .flex { display: flex; }
  .flex-col { display: flex; flex-direction: column; }
  .gap-sm { gap: 8px; }
  .gap-md { gap: 16px; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .w-full { width: 100%; }
  .text-center { text-align: center; }
  .mt-sm { margin-top: 8px; }
  .mt-md { margin-top: 16px; }
  .mb-sm { margin-bottom: 8px; }
  .mb-md { margin-bottom: 16px; }

  /* ── Code ── */
  code {
    font-family: 'Fira Code', Consolas, monospace;
    font-size: 13px;
    background: var(--bg-paper);
    padding: 2px 6px;
    border-radius: var(--radius-sm);
  }
  pre {
    background: var(--bg-paper);
    padding: 12px;
    border-radius: var(--radius);
    overflow-x: auto;
    font-size: 13px;
  }
  pre code { background: none; padding: 0; }
</style>
`;

/**
 * Wraps raw user HTML with security headers, theme CSS, and the bridge script.
 * The CSP meta tag and theme CSS go in <head>, the bridge script goes at the end of <body>.
 *
 * @param {string} rawHtml - The LLM-generated HTML
 * @param {string} themeMode - "light" or "dark"
 */
function buildSrcdoc(rawHtml, themeMode) {
    const headInjection = CSP_META + FONT_LINK + THEME_CSS;
    const themeAttr = themeMode === "dark" ? ' data-theme="dark"' : "";

    // If the HTML already has a <head>, inject into it.
    if (rawHtml.includes("<head>")) {
        return rawHtml
            .replace("<head>", `<head>${headInjection}`)
            .replace("<body", `<body${themeAttr}`)
            .replace("</body>", `${BRIDGE_SCRIPT}</body>`);
    }

    if (rawHtml.includes("<html")) {
        return rawHtml
            .replace(/<html([^>]*)>/, `<html$1><head>${headInjection}</head>`)
            .replace("<body", `<body${themeAttr}`)
            .replace("</body>", `${BRIDGE_SCRIPT}</body>`);
    }

    // No document structure — wrap it
    return `<!DOCTYPE html>
<html>
<head>${headInjection}</head>
<body${themeAttr}>
${rawHtml}
${BRIDGE_SCRIPT}
</body>
</html>`;
}

/**
 * Minimum height for the iframe to prevent collapsed artifacts.
 */
const MIN_IFRAME_HEIGHT = 60;

/**
 * Maximum height for the iframe before scroll kicks in.
 */
const MAX_IFRAME_HEIGHT = 600;

/**
 * ArtifactRenderer — renders LLM-generated HTML in a sandboxed iframe
 * with auto-height, code/preview toggle, and a sendPrompt bridge.
 *
 * Props:
 *   title          – card header text
 *   description    – optional subtitle
 *   html           – raw HTML/CSS/JS content from the LLM
 *   onSendPrompt   – (text: string) => void — called when iframe triggers sendPrompt
 */
export default function ArtifactRenderer({
    title,
    description,
    html,
    onSendPrompt,
}) {
    const theme = useTheme();
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(MIN_IFRAME_HEIGHT);
    const [showCode, setShowCode] = useState(false);

    /**
     * Listen for postMessage events from the sandboxed iframe.
     * Handles both sendPrompt bridge calls and auto-resize reports.
     */
    const handleMessage = useCallback(
        (event) => {
            const data = event.data;
            if (!data || typeof data !== "object") return;

            // Only accept messages from our iframe
            if (
                iframeRef.current &&
                event.source !== iframeRef.current.contentWindow
            ) {
                return;
            }

            if (
                data.type === "artifact-send-prompt" &&
                typeof data.text === "string"
            ) {
                onSendPrompt && onSendPrompt(data.text);
            }

            if (
                data.type === "artifact-resize" &&
                typeof data.height === "number"
            ) {
                const clampedHeight = Math.max(
                    MIN_IFRAME_HEIGHT,
                    Math.min(data.height, MAX_IFRAME_HEIGHT),
                );
                setIframeHeight(clampedHeight);
            }
        },
        [onSendPrompt],
    );

    useEffect(() => {
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handleMessage]);

    const srcdoc = buildSrcdoc(html, theme.palette.mode);

    return (
        <Box
            sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                my: 1,
            }}
        >
            {/* Card header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        color: theme.palette.primary.main,
                    }}
                >
                    <StarsMinimalistic size={16} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {title}
                    </Typography>
                    {description && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.2 }}
                        >
                            {description}
                        </Typography>
                    )}
                </Box>

                {/* Code/preview toggle */}
                <IconButton
                    size="small"
                    onClick={() => setShowCode((prev) => !prev)}
                    aria-label={showCode ? "Show preview" : "Show code"}
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1.5,
                        p: 0.5,
                    }}
                >
                    {showCode ? <Eye size={16} /> : <Code size={16} />}
                </IconButton>
            </Box>

            {/* Code view */}
            <Collapse in={showCode}>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        fontSize: 12,
                        fontFamily: "'Fira Code', 'Consolas', monospace",
                        overflow: "auto",
                        maxHeight: MAX_IFRAME_HEIGHT,
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? "rgba(0,0,0,0.3)"
                                : "rgba(0,0,0,0.04)",
                        color: theme.palette.text.secondary,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {html}
                </Box>
            </Collapse>

            {/* Live preview — sandboxed iframe */}
            <Collapse in={!showCode}>
                <Box
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        srcDoc={srcdoc}
                        sandbox="allow-scripts"
                        referrerPolicy="no-referrer"
                        title={title}
                        style={{
                            width: "100%",
                            height: iframeHeight,
                            border: "none",
                            display: "block",
                        }}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}
